import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DataTable,
    DropdownMenu,
    StatusTag,
    type ColumnConfig,
    type DropdownMenuItem,
} from '@/components/ui';
import { appTileLabel } from './coverageHelpers';
import {
    AppNotApprovedEmptyState,
    NoAppSelectedEmptyState,
    NoRoleKeysEmptyState,
} from './EmptyStates';
import { CatalogDrawer } from './CatalogDrawer';
import { CatalogSyncResultNotice } from './CatalogSyncResultNotice';
import { RemoveLinkConfirmModal, type RemoveLinkTarget } from './RemoveLinkConfirmModal';
import { RoleEditorChipsCell, RoleEditorIdentityCell } from './RoleEditorRowCells';
import { UndoBanner } from './UndoBanner';
import type { EditorRoleRow } from './types';
import type { RoleCoverageVM } from './useRoleCoverage';
import shared from './coverageShared.module.css';
import styles from './RoleEditorCard.module.css';

/**
 * EAA-1817: the selected app's editor — header (identity, staleness line,
 * "Đồng bộ danh mục"/"Xem danh mục"), the "Vai trò chuẩn | Vai trò trong
 * {app}" table, the footer counts, and the undo banner for a just-removed
 * chip.
 *
 * Loads independently of the coverage board (frame 60's loading note: "Bảng phủ
 * và panel tải riêng") — its own queries (`fetchRoleMaps`, `fetchAppRoles`) are
 * separate from the coverage query the rail and the matrix read, so neither
 * blocks the other.
 */
export function RoleEditorCard({ vm }: { vm: RoleCoverageVM }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { board, editor, roleKeyCreate, canManage } = vm;
    const app = board.selectedAppRow;
    const tableContainerRef = useRef<HTMLDivElement>(null);
    // EAA-1817 (frame 55g): which link the confirm dialog is about, or null.
    const [removeTarget, setRemoveTarget] = useState<RemoveLinkTarget | null>(null);

    // EAA-1817: "Nhấp một ô để mở đúng app + vai trò đó" — a matrix click sets
    // `board.focusRequest`; once this app's rows are rendered, scroll the
    // matching role row into view and move focus onto it.
    //
    // EAA-1884: the table is now the kit `DataTable` (antd `Table` under the
    // hood), which has no `onRow` passthrough to attach a per-row ref/tabIndex
    // the way the old hand-rolled `<tr ref={rowRef} tabIndex={-1}>` did. Every
    // antd table row DOES carry its own `data-row-key` attribute (from
    // `rowKey="roleKey"` below), so scope a query to this table's container
    // instead of keeping a `Map<roleKey, HTMLTableRowElement>` built from a
    // per-row ref callback. `tabIndex` is set here, right before focusing,
    // rather than permanently on every row — functionally identical (it only
    // ever mattered at the moment of a programmatic `.focus()` call).
    useEffect(() => {
        if (!board.focusRequest) return;
        const container = tableContainerRef.current;
        if (!container) return;
        const row = container.querySelector<HTMLElement>(
            `[data-row-key="${CSS.escape(board.focusRequest.roleKey)}"]`
        );
        if (!row) return;
        row.tabIndex = -1;
        row.scrollIntoView({ block: 'nearest' });
        row.focus();
    }, [board.focusRequest, editor.editorRows]);

    // EAA-1884: destructured out of `editor` (rather than referenced as
    // `editor.x` inside the memo below) so the dep array can list these
    // individually instead of the whole `editor` object — react-hooks/
    // exhaustive-deps otherwise asks for the whole object once a callback
    // calls one of its methods (`editor.addRole(...)`), since a method call
    // can rely on more of the object than a plain property read proves.
    const {
        appRoleOptions,
        appRolesLoading,
        openPickerRoleKey,
        setOpenPickerRoleKey,
        addRole,
        removePending,
    } = editor;

    // EAA-1884: kept above every early return — hooks must run unconditionally,
    // and `columns` is only ever read once `app` is non-null (below).
    const columns = useMemo<ColumnConfig<EditorRoleRow>[]>(
        () => [
            {
                key: 'standardRole',
                title: t('console.roleCoverage.editorCard.table.columnStandardRole'),
                /* Frame 55 declares `th` widths 24% / 42% / 12% / 16% / 52px over
                   FIVE columns. "Tài khoản" (12%) is absent here — no endpoint
                   counts accounts per standard role across every organization
                   (see the note on the status column) — so its share is
                   redistributed across the three that remain, keeping the frame's
                   24:42:16 proportions intact rather than letting the app-role
                   column swallow it, which is what happened with no widths at
                   all: 21/61/18 instead of 29/51/20. */
                width: '29%',
                render: (_value, row) => <RoleEditorIdentityCell row={row} />,
            },
            {
                key: 'appRole',
                title: t('console.roleCoverage.editorCard.table.columnAppRole', {
                    appName: app?.name ?? '',
                }),
                width: '51%',
                render: (_value, row) => (
                    <RoleEditorChipsCell
                        row={row}
                        appName={app?.name ?? ''}
                        canManage={canManage}
                        appRoleOptions={appRoleOptions}
                        appRolesLoading={appRolesLoading}
                        isPickerOpen={openPickerRoleKey === row.roleKey}
                        onOpenPicker={() => setOpenPickerRoleKey(row.roleKey)}
                        onClosePicker={() => setOpenPickerRoleKey(null)}
                        onAddRole={(appRole) => addRole(row.roleKey, appRole)}
                        onRequestRemoveChip={(chip) =>
                            setRemoveTarget({
                                id: chip.id,
                                roleKey: row.roleKey,
                                appRole: chip.appRole,
                                roleName: row.roleName || row.roleKey,
                                appRoleName: chip.appRoleName,
                                appName: app?.name ?? '',
                            })
                        }
                        removePending={removePending}
                    />
                ),
            },
            {
                /**
                 * EAA-1817 (frame 55's fourth column). Derived from the row's own
                 * chips, not fetched: a chip the app has dropped (`isStale`) means
                 * the link is paused, no chips at all means the standard role is
                 * not covered here, and anything else is running. Those are the
                 * same three words the list view's "Tình trạng" column uses.
                 *
                 * Frame 55's THIRD column, "Tài khoản", is NOT here: no endpoint
                 * counts accounts per standard role across every organization,
                 * and this board is ecosystem-default by construction (the
                 * coverage endpoint takes no org param). `useAccountCounts` is
                 * org-scoped, so reusing it would print a number for ONE org
                 * under a heading that reads as a total. Recorded as a gap rather
                 * than fabricated — see the SRS.
                 */
                key: 'status',
                title: t('console.roleCoverage.editorCard.table.columnStatus'),
                width: '20%',
                render: (_value, row) => {
                    const status = row.chips.some((chip) => chip.isStale)
                        ? 'paused'
                        : row.chips.length === 0
                          ? 'uncovered'
                          : 'active';
                    return (
                        <StatusTag
                            status={status}
                            config={{
                                active: {
                                    label: t('console.roleCoverage.roleAxisCard.stateActive'),
                                    tone: 'success',
                                },
                                uncovered: {
                                    label: t('console.roleCoverage.roleAxisCard.stateUncovered'),
                                    tone: 'warning',
                                },
                                paused: {
                                    label: t('console.roleCoverage.roleAxisCard.statePaused'),
                                    tone: 'error',
                                },
                            }}
                        />
                    );
                },
            },
        ],
        [
            t,
            app?.name,
            canManage,
            appRoleOptions,
            appRolesLoading,
            openPickerRoleKey,
            setOpenPickerRoleKey,
            addRole,
            removePending,
        ]
    );

    /**
     * Frame 55f's row menu. Built per row rather than memoised: it closes over the
     * row and the app, both of which change with the selection, and a menu is
     * mounted only while its trigger is open.
     */
    function rowMenuItems(row: EditorRoleRow): DropdownMenuItem[] {
        const items: DropdownMenuItem[] = [];
        const firstChip = row.chips[0];

        // "Sửa liên kết" — the edit IS the picker, so open it rather than
        // inventing a second editing surface.
        if (canManage) {
            items.push({
                key: 'edit',
                label: t('console.roleCoverage.rowMenu.edit'),
                onSelect: () => setOpenPickerRoleKey(row.roleKey),
            });
        }

        // "Đổi phạm vi áp dụng" — this board is ecosystem-default by
        // construction (the coverage endpoint takes no org param), so the scope
        // switch lives on the list view. Send the reader there instead of
        // offering a control that cannot act here.
        items.push({
            key: 'scope',
            label: t('console.roleCoverage.rowMenu.changeScope'),
            onSelect: () => navigate('/console/role-map?view=list'),
        });

        if (canManage && firstChip) {
            items.push({
                key: 'remove',
                label: t('console.roleCoverage.rowMenu.remove'),
                danger: true,
                onSelect: () =>
                    setRemoveTarget({
                        id: firstChip.id,
                        roleKey: row.roleKey,
                        appRole: firstChip.appRole,
                        roleName: row.roleName || row.roleKey,
                        appRoleName: firstChip.appRoleName,
                        appName: app?.name ?? '',
                    }),
            });
        }

        return items;
    }

    /**
     * app-role key → the standard roles already linked to it, and the set the app
     * has stopped reporting. Both are derived from the rows already on screen, so
     * the drawer costs no request of its own.
     */
    const linkedByAppRole = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const row of editor.editorRows) {
            for (const chip of row.chips) {
                const list = map.get(chip.appRole) ?? [];
                list.push(row.roleName || row.roleKey);
                map.set(chip.appRole, list);
            }
        }
        return map;
    }, [editor.editorRows]);

    /** app-role key → display name, from the catalog this card already loaded. */
    const resolveAppRoleName = useCallback(
        (key: string) => editor.appRoleOptions.find((option) => option.key === key)?.name || key,
        [editor.appRoleOptions]
    );

    const lostAppRoles = useMemo(() => {
        const keys = new Set<string>();
        for (const row of editor.editorRows) {
            for (const chip of row.chips) {
                if (chip.isStale) keys.add(chip.appRole);
            }
        }
        return keys;
    }, [editor.editorRows]);

    if (!app) {
        return <NoAppSelectedEmptyState />;
    }

    if (!app.enabled) {
        return (
            <AppNotApprovedEmptyState
                appCode={app.code}
                appName={app.name}
                canManageApps={vm.canManage}
                onOpenApp={() => navigate('/console/apps')}
            />
        );
    }

    if (vm.roleKeyCount === 0) {
        return <NoRoleKeysEmptyState canManage={canManage} form={roleKeyCreate} />;
    }

    return (
        <section
            className={`hz-card ${styles.editorCard}`}
            aria-label={t('console.roleCoverage.editorCard.sectionAriaLabel', {
                appName: app.name,
            })}
        >
            <div className={styles.editorHeader}>
                <span className={shared.appTile} aria-hidden="true">
                    {appTileLabel(app.code)}
                </span>
                <div className={styles.editorHeaderText}>
                    <h2 className={shared.cardTitle}>{app.name}</h2>
                    <p className="portal-admin-error">
                        {t('console.roleCoverage.editorCard.catalogSyncLine', {
                            ageLabel: editor.catalogAgeLabel,
                        })}
                        {editor.staleCount > 0 &&
                            t('console.roleCoverage.editorCard.staleNote', {
                                count: editor.staleCount,
                                appName: app.name,
                            })}
                    </p>
                </div>
                {/* EAA-1804: was `className="portal-btn"` on already-kit `Button`s — a
                    leftover EAA-1841/EAA-1884 partial migration that kept the legacy class
                    instead of dropping it; a plain `<Button>` needs no className to render
                    correctly. See AccountDetailModal.tsx for the mapping rationale. */}
                <span className={styles.editorHeaderActions}>
                    {/* EAA-1817: the per-organization "Tạo vai trò" button that used to
                        live here is REMOVED, not hidden. This phase manages system /
                        ecosystem-default app roles only; `CreateAppRoleModal` always
                        upserts an ORG-SCOPED `AppRole`, while this coverage board reads
                        `organization__isnull=True` rows exclusively (see
                        `useRoleCoverageData`'s docstring) — so a role created from this
                        button provably could never appear on the very board that offered
                        to create it. That mismatch is why `roleCreatedCallout` existed
                        below; it is gone for the same reason. Bringing this back requires
                        the coverage board to read org-scoped rows too (a scope selector
                        here), not just re-adding the button. */}
                    {editor.canSync && (
                        <Button
                            disabled={editor.syncMutation.isPending}
                            onClick={() => editor.syncMutation.mutate()}
                        >
                            {editor.syncMutation.isPending
                                ? t('console.roleCoverage.editorCard.syncPending')
                                : t('console.roleCoverage.editorCard.syncButton')}
                        </Button>
                    )}
                    <Button
                        disabled={!editor.canOpenCatalog}
                        title={
                            editor.canOpenCatalog
                                ? undefined
                                : t('console.roleCoverage.editorCard.viewCatalogDisabledTitle')
                        }
                        aria-expanded={editor.catalogViewerOpen}
                        onClick={() => editor.setCatalogViewerOpen((open) => !open)}
                    >
                        {t('console.roleCoverage.editorCard.viewCatalogButton', {
                            count: app.catalogRoleCount,
                        })}
                    </Button>
                </span>
            </div>

            {/* EAA-1817 (frame 55e): the catalog is a DRAWER now. It used to be a
                `<ul>` that expanded inside this card, pushing the role table down
                — so reading the catalog and reading the links it feeds became
                mutually exclusive, which is the one comparison the list exists to
                support. */}
            <CatalogDrawer
                open={editor.catalogViewerOpen && editor.canOpenCatalog}
                appName={app.name}
                options={editor.appRoleOptions}
                loading={editor.appRolesLoading}
                linkedByAppRole={linkedByAppRole}
                lostAppRoles={lostAppRoles}
                onClose={() => editor.setCatalogViewerOpen(false)}
                onLink={
                    canManage
                        ? (appRoleKey) => {
                              // A link needs a standard role to attach to, and the
                              // catalog knows only the app half of the pair — so
                              // this closes the drawer and opens the picker on the
                              // first row that has no link yet, which is where the
                              // operator has to choose the other half anyway.
                              const target =
                                  editor.editorRows.find((row) => row.chips.length === 0) ??
                                  editor.editorRows[0];
                              if (target) setOpenPickerRoleKey(target.roleKey);
                              editor.setCatalogViewerOpen(false);
                              void appRoleKey;
                          }
                        : undefined
                }
            />

            {editor.writeError && <p className="portal-admin-error">{editor.writeError}</p>}

            <div className="portal-table-scroll" ref={tableContainerRef}>
                <DataTable<EditorRoleRow>
                    className="rc-role-editor-table"
                    data={editor.editorRows}
                    columns={columns}
                    rowKey="roleKey"
                    pagination={false}
                    rowClassName="rc-role-row"
                    /* EAA-1817 (frame 55f): the row's own "⋯". Frame 55 draws one
                       per row and this table had none — every action on a link was
                       reachable only by aiming at a chip. The five items are the
                       frame's, minus the two with nowhere to go: "Xem N tài khoản"
                       and "Xem nhật ký thay đổi" have no per-link destination in
                       this app (the audit log is not filterable by role-map row,
                       and there is no per-link account list), so they are recorded
                       as gaps rather than shipped as dead menu entries. */
                    actions={(row) => (
                        <DropdownMenu
                            items={rowMenuItems(row)}
                            triggerAriaLabel={t('console.roleCoverage.rowMenu.triggerAriaLabel', {
                                roleName: row.roleName || row.roleKey,
                            })}
                        />
                    )}
                    /* Frame 55's row-menu column is 52px. */
                    actionsWidth={52}
                    // EAA-1884: WHY empty — the default `scroll={{x:900}}` makes
                    // antd apply `overflow-y: hidden` on the scroll wrapper
                    // whenever `scroll.x` is set (see `@rc-component/table`'s
                    // `Table.js`), which would clip `RolePickerPopover` (an
                    // absolutely-positioned popover that intentionally extends
                    // below its row, not a portal-rendered overlay). This table
                    // only ever has two columns and never needed horizontal
                    // scroll.
                    scroll={{}}
                />
            </div>

            {/* EAA-1817 (frame 55g): removing a link now asks first, and says how
                many people lose access. It used to happen on one click, with the
                10s undo banner as the only safety net — see the modal's own
                docstring for why that is the wrong shape for this action. */}
            <RemoveLinkConfirmModal
                target={removeTarget}
                pending={editor.removePending}
                resolveAppRoleName={resolveAppRoleName}
                onCancel={() => setRemoveTarget(null)}
                onConfirm={() => {
                    if (!removeTarget) return;
                    editor.removeChip(removeTarget.roleKey, {
                        id: removeTarget.id,
                        appRole: removeTarget.appRole,
                    });
                    setRemoveTarget(null);
                }}
            />

            {/* EAA-1817 (frame 55d): what the sync just did, with the two
                follow-ups the frame offers. */}
            {editor.syncResult && (
                <CatalogSyncResultNotice
                    result={editor.syncResult}
                    appName={app.name}
                    onViewCatalog={() => {
                        editor.setCatalogViewerOpen(true);
                        editor.dismissSyncResult();
                    }}
                    onHandleLostRoles={() => {
                        // The lost roles ARE the stale rows already flagged in
                        // this table; scroll to the first one rather than opening
                        // a second surface listing the same thing.
                        const firstStale = editor.editorRows.find((row) =>
                            row.chips.some((chip) => chip.isStale)
                        );
                        if (firstStale) {
                            tableContainerRef.current
                                ?.querySelector<HTMLElement>(
                                    `[data-row-key="${CSS.escape(firstStale.roleKey)}"]`
                                )
                                ?.scrollIntoView({ block: 'nearest' });
                        }
                        editor.dismissSyncResult();
                    }}
                    onDismiss={editor.dismissSyncResult}
                />
            )}

            <div className={styles.editorFooter}>
                <span className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.footer.summary', {
                        roleKeyCount: vm.roleKeyCount,
                        totalLinks: editor.totalLinks,
                        appName: app.name,
                    })}
                </span>
                <span className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.footer.hint')}
                </span>
            </div>

            <UndoBanner items={editor.pendingUndos} onUndo={editor.undoRemove} />
        </section>
    );
}

export default RoleEditorCard;
